// ---------------------------------------------------------------------------
// Onde os overlays com portal aterrissam.
//
// `createPortal` decide a caixa pelo NÓ DE DESTINO, não pela posição no React.
// Os cinco portais do app miravam `document.body` — e `Modal` e `Sheet` são os
// primitivos que quase todo diálogo usa. Com o frame de desktop isso vira um
// defeito visível: o app fica numa coluna de 430px e o diálogo abre com a
// largura inteira da janela, porque `document.body` está FORA do frame. Mover o
// componente para dentro do frame no JSX não resolveria nada — o portal continua
// mirando o destino que recebeu.
//
// A saída é o destino ser o próprio frame. `#app-overlay-host` é irmão do
// scroller e filho do frame, então:
//  · no desktop, o frame tem `translate` e vira containing block — todo
//    `position: fixed` que aterrissa aqui fica contido nos 430px E ancorado,
//    porque o frame não é quem rola (medido: dentro do scroller o mesmo
//    elemento rolaria embora);
//  · no mobile o frame não tem transform nenhum, então `fixed` volta a resolver
//    contra a viewport — exatamente o comportamento de sempre.
//
// Fora do frame (landing, institucionais, login, /pro, /founder, /admin) o host
// não existe e a função devolve `document.body`, que é o destino histórico.
// Por isso a troca é segura de aplicar nos cinco call sites de uma vez: onde
// não há frame, nada muda.
// ---------------------------------------------------------------------------

/** Casca do frame — é ela que carrega o `translate` no desktop. */
export const APP_FRAME_ID = "app-frame";

/** Nó irmão do scroller onde os portais aterrissam. */
export const APP_OVERLAY_HOST_ID = "app-overlay-host";

/**
 * Destino de portal para overlays (modais, sheets, toasts, popovers).
 *
 * Devolve `document.body` quando não há frame montado — o comportamento
 * anterior — em vez de lançar. Um diálogo que não abre é pior que um diálogo
 * largo demais.
 */
export function getOverlayHost(): HTMLElement {
  if (typeof document === "undefined") {
    // Sem DOM não há portal a criar; quem chama já está em ambiente de browser.
    // O cast mantém a assinatura simples para os call sites.
    return null as unknown as HTMLElement;
  }
  return document.getElementById(APP_OVERLAY_HOST_ID) ?? document.body;
}
